import { FunctionComponent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faCaretUp } from "@fortawesome/pro-solid-svg-icons";

import { getSuggestedCopywritingInstruction } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";

import { useAppSelector } from "@/redux/hooks";

import {
	OutreachTemplateScopeArgs,
	useGetOutreachInstructionTemplatesQuery,
} from "@/pages/Private/redux/list/list-outreach-template.api";

import { outreachTemplatesSelector } from "@/pages/Private/redux/list/list-outreach-template.slice";

import { OutreachInstructionTemplate } from "../../../schema/outreach-instruction-template.schema";
import { InstructionMenuPopover } from "./InstructionMenuPopover";

interface InstructionTemplatesMenuProps {
	/** Which shelf of the library to list — for this section, COPYWRITING × the list's source type. */
	scope: OutreachTemplateScopeArgs;
	/** The template currently loaded, highlighted in the list. */
	loadedTemplateId: number | null;
	onSelect: (template: OutreachInstructionTemplate) => void;
	/** Load the source type's built-in suggestion. Omit it to hide the *Suggested* group. */
	onSelectSuggestion?: (body: string) => void;
	portalContainer?: HTMLElement | null;
}

/**
 * The searchable **Templates** dropdown (PREC-3687 AC 8): the company's saved instructions for
 * this list's source type, with a `Default` badge on the company default, above them the built-in
 * suggestion for that source type where one exists.
 *
 * The suggestion is the same text a **new** list of this type is born with
 * (`SUGGESTED_COPYWRITING_INSTRUCTIONS`, seeded server-side by `list-outreach-prefill.ts`), offered
 * here so a list created before it existed — or one whose instruction was cleared — is one click
 * from the same starting point. It is not a library row: selecting it loads the body with **no**
 * template pointer, so the editor treats the text as hand-typed and offers *Save as new template*.
 *
 * The query is fired here — this component owns the library, so block 6 can mount a second one for
 * the company-wide `SENDER_ROUTING` shelf without the page having to fetch anything. It is skipped
 * for an incomplete `COPYWRITING` scope: the API answers that with a named `400`
 * (`COPYWRITING_REQUIRES_SOURCE_TYPE`), deliberately not an empty list.
 *
 * The payload is read from the slice, never from the hook (web rule §2) — and `undefined` there
 * means "not fetched yet", which the menu shows as loading rather than as "no templates".
 */
export const InstructionTemplatesMenu: FunctionComponent<InstructionTemplatesMenuProps> = ({
	scope,
	loadedTemplateId,
	onSelect,
	onSelectSuggestion,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const ti = (key: string) => t(`list.outreach.general.instruction.${key}`);

	const triggerRef = useRef<HTMLButtonElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState("");

	const isScopeIncomplete = scope.kind === "COPYWRITING" && !scope.listSourceType;

	useGetOutreachInstructionTemplatesQuery(scope, { skip: isScopeIncomplete });

	const templates = useAppSelector(
		outreachTemplatesSelector(isScopeIncomplete ? undefined : scope)
	);

	const close = () => {
		setIsOpen(false);
		setSearch("");
	};

	const suggestion = onSelectSuggestion
		? getSuggestedCopywritingInstruction(scope.listSourceType)
		: null;
	const suggestionName = suggestion ? ti(`suggestions.${suggestion.nameKey}`) : "";

	const needle = search.trim().toLowerCase();
	const visible = (templates ?? []).filter((template) =>
		template.name.toLowerCase().includes(needle)
	);
	const isSuggestionVisible = suggestion !== null && suggestionName.toLowerCase().includes(needle);

	const renderSuggestion = () => {
		if (!suggestion || !isSuggestionVisible) {
			return null;
		}

		return (
			<>
				<div className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-text-meta">
					{ti("suggestedTemplates")}
				</div>
				<button
					className="flex w-full cursor-pointer items-start gap-2 px-3 py-1.5 text-left duration-150 hover:bg-selected-item"
					data-testid="instruction-suggestion"
					type="button"
					onClick={() => {
						onSelectSuggestion?.(suggestion.body);
						close();
					}}
				>
					<span className="min-w-0 flex-1">
						<span className="block truncate text-xs font-semibold text-text">{suggestionName}</span>
						<span className="block truncate text-[11px] text-text-meta">
							{suggestion.body.split("\n")[0]}
						</span>
					</span>
				</button>
			</>
		);
	};

	const renderRows = () => {
		// Only the library is fetched; the suggestion is local, so it is shown straight away rather
		// than held behind the request that has nothing to do with it.
		if (templates === undefined) {
			return (
				<>
					{renderSuggestion()}
					<p className="px-3 py-2 text-xs italic text-text-meta">{ti("loading")}</p>
				</>
			);
		}

		if (templates.length === 0) {
			return (
				<>
					{renderSuggestion()}
					{!isSuggestionVisible && (
						<p className="px-3 py-2 text-xs italic text-text-meta">{ti("noTemplates")}</p>
					)}
				</>
			);
		}

		if (visible.length === 0) {
			return (
				<>
					{renderSuggestion()}
					{!isSuggestionVisible && (
						<p className="px-3 py-2 text-xs italic text-text-meta">{ti("noMatches")}</p>
					)}
				</>
			);
		}

		return (
			<>
				{renderSuggestion()}
				<div className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-text-meta">
					{ti("savedTemplates")}
				</div>
				{visible.map((template) => (
					<button
						key={template.id}
						className={`flex w-full cursor-pointer items-start justify-between gap-2 px-3 py-1.5 text-left duration-150 hover:bg-selected-item ${
							template.id === loadedTemplateId ? "bg-selected-item" : ""
						}`}
						type="button"
						onClick={() => {
							onSelect(template);
							close();
						}}
					>
						<span className="min-w-0 flex-1">
							<span className="block truncate text-xs font-semibold text-text">
								{template.name}
							</span>
							<span className="block truncate text-[11px] text-text-meta">
								{template.body.split("\n")[0]}
							</span>
						</span>
						{template.isDefault && (
							<span className="shrink-0 rounded-full bg-light-blue px-2 py-0.5 text-[10px] font-semibold text-brand-light">
								{ti("defaultBadge")}
							</span>
						)}
					</button>
				))}
			</>
		);
	};

	return (
		<>
			<button
				ref={triggerRef}
				className="flex h-[30px] cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-semibold text-text duration-200 hover:border-brand-light"
				type="button"
				onClick={() => (isOpen ? close() : setIsOpen(true))}
			>
				{ti("templates")}
				<Icon className="h-3 w-3 text-text-meta" icon={faCaretUp} />
			</button>
			{isOpen && (
				<InstructionMenuPopover
					anchorEl={triggerRef.current}
					portalContainer={portalContainer}
					searchName="instruction-templates"
					searchPlaceholder={ti("searchTemplates")}
					searchValue={search}
					onClose={close}
					onSearchChange={setSearch}
				>
					{renderRows()}
				</InstructionMenuPopover>
			)}
		</>
	);
};
